import { faCheck, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, ButtonProps, Group, Popover, PopoverProps, Text } from '@mantine/core';
import React, { cloneElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { If } from '../If';
import { TooltipCardContainer } from '../TooltipCard';
import classes from './PopConfirm.module.css';

interface PopConfirmTriggerProps {
  onClick?: React.MouseEventHandler<HTMLElement>;
  onKeyDown?: React.KeyboardEventHandler<HTMLElement>;
  ref?: React.Ref<HTMLElement>;
}

export interface PopConfirmProps {
  autoToggle?: boolean;
  btnCancelLabel?: string;
  btnConfirmLabel?: string;
  cancelBtnProps?: ButtonProps;
  closeOnClickOutside?: boolean;
  confirmBtnProps?: ButtonProps;
  description?: string;
  disabled?: boolean;
  onCancel?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onConfirm: (e: React.MouseEvent<HTMLButtonElement>) => void;
  opened?: boolean;
  position?: PopoverProps['position'];
  title: string;
  width?: number;
  withArrow?: boolean;
  children: React.ReactElement<PopConfirmTriggerProps>;
}

export const PopConfirm = React.forwardRef<HTMLElement, PopConfirmProps>(
  (
    {
      autoToggle = true,
      btnCancelLabel,
      btnConfirmLabel,
      cancelBtnProps,
      children,
      closeOnClickOutside = true,
      confirmBtnProps,
      description,
      disabled,
      onCancel,
      onConfirm,
      opened: openedProp,
      title,
      width = 215,
      withArrow,
      position,
    }: PopConfirmProps,
    ref,
  ) => {
    const [opened, setOpened] = useState<boolean>(openedProp || false);
    const { t } = useTranslation();

    const handleConfirm = (e: React.MouseEvent<HTMLButtonElement>) => {
      onConfirm(e);
    };

    const handleCancel = (e: React.MouseEvent<HTMLButtonElement>) => {
      setOpened(false);
      onCancel?.(e);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
      if (e.key === 'Escape' && opened) {
        setOpened(false);
      }
    };

    useEffect(() => {
      if (typeof openedProp === 'boolean') {
        setOpened(openedProp);
      }
    }, [openedProp]);

    return (
      <Popover
        opened={opened}
        onChange={setOpened}
        onClose={() => setOpened(false)}
        width={width}
        closeOnClickOutside={closeOnClickOutside}
        disabled={disabled}
        withArrow={withArrow}
        {...(position ? { position } : {})}
      >
        <Popover.Target>
          {cloneElement(children, {
            ref,
            onClick: (e: React.MouseEvent<HTMLElement>) => {
              children.props.onClick?.(e);

              if (autoToggle) {
                setOpened(!opened);
              }
            },
            onKeyDown: (e: React.KeyboardEvent<HTMLElement>) => {
              children.props.onKeyDown?.(e);
              handleKeyDown(e);
            },
          })}
        </Popover.Target>
        <Popover.Dropdown className={classes.popConfirmDropdown}>
          <TooltipCardContainer>
            <Text mb={10} fz={14} fw={500}>
              {title}
            </Text>
            <If value={!!description}>
              <Text size="xs" mb={10}>
                {description}
              </Text>
            </If>

            <Group justify="flex-end" gap={10}>
              <Button
                leftSection={<FontAwesomeIcon icon={faCheck} fontSize={14} />}
                size="compact-xs"
                variant="outline"
                onClick={handleConfirm}
                {...confirmBtnProps}
              >
                {btnConfirmLabel || t('confirm')}
              </Button>
              <Button
                leftSection={<FontAwesomeIcon icon={faXmark} fontSize={14} />}
                size="compact-xs"
                variant="outline"
                onClick={handleCancel}
                {...cancelBtnProps}
              >
                {btnCancelLabel || t('cancel')}
              </Button>
            </Group>
          </TooltipCardContainer>
        </Popover.Dropdown>
      </Popover>
    );
  },
);
